import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MATERIAL_CATEGORIES } from "@/pages/factory/FactoryOrdersPage";

const STATUS_TONE = {
  pending:     "border-gray-500/40 text-gray-400 bg-gray-500/5",
  received:    "border-sky-500/40 text-sky-300 bg-sky-500/5",
  in_progress: "border-[#d4af37]/40 text-[#d4af37] bg-[#d4af37]/5",
  dispatched:  "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
  rejected:    "border-red-500/40 text-red-400 bg-red-500/5",
};
const NEXT_STATUSES = ["received", "in_progress", "dispatched", "rejected"];

// Which receiving-team keys does this role belong to?
const TEAM_ROLES = {
  factory:     ["Production Manager", "Production Executive"],
  purchase:    ["Purchase Manager", "Purchase Executive"],
  imos:        ["IMOS Manager", "IMOS Executive", "Imos Manager", "Imos Executive"],
  procurement: ["Procurement Manager", "Procurement Executive"],
  design:      ["Interior Designer", "Design Manager", "Design Head"],
};
const TEAM_LABEL = { factory: "Factory", purchase: "Purchase", imos: "IMOS", procurement: "Procurement", design: "Design" };

function callerTeamKeys(isAdmin, roleName, roleType) {
  if (isAdmin || roleType === "director") return Object.keys(TEAM_ROLES);
  return Object.entries(TEAM_ROLES)
    .filter(([, roles]) => roles.includes(roleName))
    .map(([k]) => k);
}

export default function MaterialRequestsInboxPage() {
  const { isAdmin, roleName, roleType } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const myTeams = useMemo(
    () => callerTeamKeys(isAdmin, roleName, roleType),
    [isAdmin, roleName, roleType]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/material-requests?scope=incoming");
      setRows(data.requests || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (r, status) => {
    let reason = null;
    if (status === "rejected") {
      reason = window.prompt("Reject reason (required):");
      if (!reason || !reason.trim()) { toast.error("Reason required"); return; }
    }
    try {
      await api.patch(`/material-requests/${r.id}/status`, { status, reason });
      toast.success(`Updated to ${status.replace("_", " ")}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const matLabel = (k) => MATERIAL_CATEGORIES.find((c) => c.key === k)?.label || k;

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-6 md:px-10 py-6 bg-[#050505]/80 sticky top-0 z-30">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Inbox</p>
        <h1 className="font-display text-2xl md:text-3xl text-white mt-1">Material Requests</h1>
        <p className="text-[11px] text-gray-500 mt-1 italic">
          Requests routed to your team by Site Supervisors and Project Managers.
        </p>
      </div>
      <div className="px-6 md:px-10 py-6 space-y-3">
        {loading ? (
          <div className="iz-card p-6 flex items-center gap-2 text-xs text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin"/> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="iz-card p-8 text-center text-sm text-gray-500 italic">
            No incoming requests right now.
          </div>
        ) : rows.map((r) => {
          // Show status specific to the caller's own team(s) for this request.
          const relevantTeams = (r.send_to || []).filter((t) => myTeams.includes(t));
          const primaryTeam = relevantTeams[0];
          const teamStatus = (primaryTeam && r.team_status?.[primaryTeam]?.status) || "pending";
          return (
            <div key={r.id} className="iz-card p-4" data-testid={`inbox-request-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm">
                    {r.client_name || "—"}
                    {r.project_name ? <span className="text-gray-500"> · {r.project_name}</span> : null}
                    {r.priority === "urgent" && (
                      <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-red-400 border border-red-500/40 rounded px-1 py-0.5">
                        Urgent
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-1">
                    <span className="text-gray-500">Category:</span> {matLabel(r.material_category)}
                  </p>
                  <p className="text-[11px] text-gray-300 mt-1 whitespace-pre-wrap">{r.description}</p>
                  {r.quantity && <p className="text-[11px] text-gray-500 mt-1">Qty: {r.quantity}</p>}
                  {r.notes && <p className="text-[11px] text-gray-500 mt-1">Notes: {r.notes}</p>}
                  {relevantTeams.length > 0 && (
                    <p className="text-[10px] text-gray-500 mt-2">
                      Routed to your team: {relevantTeams.map((t) => TEAM_LABEL[t]).join(" · ")}
                    </p>
                  )}
                  <p className="text-[10px] text-gray-600 mt-2">
                    Raised by {r.requested_by_name} · {new Date(r.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="shrink-0 space-y-2">
                  <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${STATUS_TONE[teamStatus]}`}
                        data-testid={`inbox-status-${r.id}`}>
                    {teamStatus.replace("_", " ")}
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {NEXT_STATUSES.map((s) => (
                      <button key={s} onClick={() => updateStatus(r, s)}
                              className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border border-[#262626] text-gray-400 hover:text-white hover:border-[#d4af37]/60 transition-colors"
                              data-testid={`inbox-set-${r.id}-${s}`}>
                        {s.replace("_", " ")}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
